import type { MouseEvent } from "react";
import { StateMessage } from "@/components/StateMessage";
import { useBillsDue } from "@/hooks/useBillsDue";
import { formatMoney } from "@/lib/format";
import "./BillsDueTile.css";

/**
 * The tile takes no props, so it navigates the way the report pages do (ReportsActivity's own handler):
 * pushState does not fire popstate, so App.tsx's listener is told directly. A modified click (new tab) is left alone.
 */
function openAgingReport(event: MouseEvent<HTMLAnchorElement>): void {
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
  event.preventDefault();
  window.history.pushState({}, "", "/reports/aging");
  window.dispatchEvent(new PopStateEvent("popstate"));
}

function countLabel(count: number): string {
  return `${count} ${count === 1 ? "bill" : "bills"}`;
}

/**
 * ef-061-3 - the Home "Bills due" tile over GET /api/demo/home/bills-due: what is owed in the next 30 days and
 * what is already overdue, each as a count and an exact total. Every figure is the API's own; nothing is computed
 * here. Rendered inside ef-061-4's wrapper (which owns the test id), takes no props.
 *
 * `role="region"` + `aria-label` and `data-state` on the SAME element (same convention as CashFlowPlannerCard).
 * A failed read stays inside this tile as a role-alert; the other tiles never notice.
 */
export function BillsDueTile() {
  const { billsDue, status, error } = useBillsDue();

  return (
    <section className="bills-due-tile" role="region" aria-label="Bills due" data-state={status}>
      <h2 className="bills-due-tile__title">Bills due</h2>

      {status === "loading" && <StateMessage tone="loading" title="Loading bills due…" />}

      {status === "error" && (
        <StateMessage tone="error" title="Couldn't load bills due." detail={error ?? undefined} />
      )}

      {status === "empty" && <StateMessage tone="empty" title="No bills due." />}

      {status === "ready" && billsDue && (
        <ul className="bills-due-tile__list">
          <li className="bills-due-tile__row">
            <span className="bills-due-tile__label">Due in the next 30 days</span>
            <span className="bills-due-tile__count">{countLabel(billsDue.due.count)}</span>
            <span className="bills-due-tile__total">{formatMoney(billsDue.due.total)}</span>
          </li>
          <li className="bills-due-tile__row bills-due-tile__row--overdue">
            <span className="bills-due-tile__label">Overdue</span>
            <span className="bills-due-tile__count">{countLabel(billsDue.overdue.count)}</span>
            <span className="bills-due-tile__total">{formatMoney(billsDue.overdue.total)}</span>
          </li>
        </ul>
      )}

      {(status === "ready" || status === "empty") && (
        <a className="bills-due-tile__link" href="/reports/aging" onClick={openAgingReport}>
          See aging report
        </a>
      )}
    </section>
  );
}
